
import { Component, Vue, Mixins } from 'vue-property-decorator';
import Dialog from '@/components/Dialog.vue';
import { Route } from 'vue-router';
import DateRange from '@/components/DateRange.vue';
import api from '@/api';
import Util from '@/utils/util';
import itemMixin from '@/minix/itemMixin';

const { voucher, product } = api;

interface voucherLogModel {
  vid: string,
  createdOn: string,
  voucherTranStatus: number,
}

@Component({
  components: { DateRange, Dialog },
})
export default class SwapOutVoucherList extends Mixins(Vue.extend(itemMixin)) {
  private data = { data: [], totalCount: 0, pageSize: 20, pageNo: 0 };

  private isAllProdcutVoucherQuery: boolean = false;

  private isLogDialogVisible: boolean = false;

  private productId: string = '';

  private logVoucherNo = '';

  private voucherLogs: Array<voucherLogModel> = [];

  private product = {
    merchantName: '',
    productName: '',
    voucherGenerationMethod: 0,
  };

  private summary = {
    totalCount: 0,
    redeemCount: 0,
    activeCount: 0,
    expiredCount: 0,
    trashCount: 0,
  }

  private statusSelect: Array<{ text: string, value: number }> = [
    { text: '未啟用', value: 0 },
    { text: '啟用', value: 1 },
    { text: '已兌換', value: 2 },
    { text: '已作廢', value: 3 },
    { text: '已過期', value: 4 },
    { text: '已鎖定', value: 5 },
  ];

  private loading = false;

  merchantList: Merchant[] = [];

  private panel = 0;

  private tableHeaders = [
    { text: '供應商', value: 'merchantName' },
    { text: '類別', value: 'productCategory' },
    { text: '兌換品項', value: 'productName' },
    { text: '序號', value: 'voucherNo' },
    { text: '狀態', value: 'voucherStatus', display: (v: number) => this.getStatusText(v) },
    { text: '兌換會員ID', value: 'publicUserId' },
    { text: '類型', value: 'createTypeName' },
    { text: '到期日', value: 'expiryDate', display: (v: string) => this.$options.filters?.dateTime(v) },
    { text: '建立日期', value: 'createdOn', display: (v: string) => this.$options.filters?.date(v) },
    { text: '異動日期', value: 'modifiedOn', display: (v: string) => this.$options.filters?.date(v) },
  ];

  private logHeaders = [
    { text: '狀態', value: 'voucherTranStatus' },
    { text: '異動日期', value: 'createdOn' },
  ];

  private get headers() {
    return this.isAllProdcutVoucherQuery
      ? this.tableHeaders : this.tableHeaders.slice(3);
  }

  private get totalCount() {
    return this.data.totalCount;
  }

  private get pageCount() {
    return Math.ceil((this.data.totalCount || 0) / (this.data.pageSize || 20));
  }

  private get collection() {
    return [...(this.data.data || [])];
  }

  private get merchantSelectOptions() {
    const mcId = this.filter.merchantCategoryId;
    return this.merchantList
      .filter((m: any) => !mcId || mcId === m.categoryId)
      .map((m: any) => ({
        text: m.merchantName,
        value: m.merchantId,
      }));
  }

  private filter = {
    merchantCategoryId: this.$route.query.merchantCategoryId ? this.$route.query.merchantCategoryId : undefined,
    merchantId: this.$route.query.merchantId ? this.$route.query.merchantId : undefined,
    categoryId: this.$route.query.categoryId ? this.$route.query.categoryId : undefined,
    voucherNo: this.$route.query.voucherNo ? this.$route.query.voucherNo : undefined,
    voucherStatus: this.$route.query.voucherStatus ? this.$route.query.voucherStatus : undefined,
    startDate: this.$route.query.startDate ? this.$route.query.startDate : undefined,
    endDate: this.$route.query.endDate ? this.$route.query.endDate : undefined,
    pageSize: Util.isNullOrUndefined(this.$route.query.pageSize)
      ? 20
      : +this.$route.query.pageSize,
    pageNo: Util.isNullOrUndefined(this.$route.query.pageNo)
      ? 1
      : +this.$route.query.pageNo,
  };

  private async updatePage(page: number) {
    this.filter.pageNo = page;
    await this.query();
  }

  private async search() {
    this.filter.pageNo = 1;
    await this.query();
  }

  private async query() {
    const query = {
      ...this.filter,
      pageSize: `${this.filter.pageSize}`,
      pageNo: `${this.filter.pageNo}`,
    };
    if (this.isQueryChanged(query)) {
      await this.$router.replace({ query });
    }
    await this.requestData();
  }

  private async exportFile() {
    const query = {
      ...this.filter,
      pageSize: 0,
      pageNo: 1,
    };
    this.loading = true;
    const filter = this.isAllProdcutVoucherQuery
      ? query : { productId: this.productId, ...query };
    const { status, data: { data } } = await voucher.list(filter);
    if (status === 1) {
      const headers = [...this.headers];
      const content = [headers.map((h) => h.text).join(), ...data.map((d: any) => headers.map(({ value, display }) => {
        const v: string = d[value];
        return display ? (display as any)(v) : v;
      }).join())].join('\n');
      const anchor = document.createElement('a');
      const blob = new Blob([content], { type: 'text/plain' });
      const objectUrl = URL.createObjectURL(blob);
      anchor.href = objectUrl;
      anchor.download = 'vouchers.txt';
      anchor.click();
      URL.revokeObjectURL(objectUrl);
    }
    this.loading = false;
  }

  private async reset() {
    this.filter = {
      merchantCategoryId: undefined,
      merchantId: undefined,
      categoryId: undefined,
      voucherNo: undefined,
      startDate: undefined,
      voucherStatus: undefined,
      endDate: undefined,
      pageSize: 20,
      pageNo: 1,
    };
    await this.query();
  }

  private isQueryChanged(query: {
    [key: string]: string | Array<string | null> | null | undefined;
  }): boolean {
    return !Object.keys(query).every(
      (key: string) => query[key]?.toString() === this.$route.query[key],
    );
  }

  private async getProduct() {
    const { data } = await product.get(this.productId);
    this.product = data || {};
  }

  private async getVoucherSummary() {
    const { data } = await voucher.summary(this.productId);
    this.summary = data;
  }

  private async showVoucherLogs(vid: string, voucherNo: string) {
    const { data } = await voucher.getVoucherLog(vid);
    this.voucherLogs = data;
    this.isLogDialogVisible = true;
    this.logVoucherNo = voucherNo;
  }

  private handleCloseDialog() {
    this.voucherLogs = [];
    this.isLogDialogVisible = false;
    this.logVoucherNo = '';
  }

  private setProductId(productId: string) {
    this.productId = productId;
  }

  private setIsAllProdcutVoucherQuery() {
    this.isAllProdcutVoucherQuery = true;
  }

  private getStatusText(status: number) {
    return this.statusSelect.find((p) => p.value === status)?.text;
  }

  private async requestData() {
    this.loading = true;
    const filter = this.isAllProdcutVoucherQuery
      ? this.filter : { productId: this.productId, ...this.filter };
    const { data } = await voucher.list(filter);
    this.data = data;
    this.loading = false;
  }

  private async beforeRouteEnter(
    to: Route,
    from: Route,
    next: (to?: ((vm: SwapOutVoucherList) => void) | boolean) => void,
  ) {
    await next(async (vm) => {
      if (to.name === 'swapOutVoucherList') {
        vm.setIsAllProdcutVoucherQuery();
      } else {
        const productId = to.params.id;
        vm.setProductId(productId);
        await vm.getProduct();
        await vm.getVoucherSummary();
      }
      await vm.requestData();
    });
  }
}
